import {
    Alert, Box, Chip, CircularProgress, Snackbar,
    TablePagination, Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import ApiServices from '../../services/ApiServices';
import Popup from '../Popup';
import GatewaySessionDetail from './GatewaySessionDetail';

const stageColor = (stage) => {
    if (!stage) return 'default';
    if (stage === 'OTA_SUCCESS') return 'success';
    if (stage === 'OTA_FAILED' || stage === 'REQUEST_REJECTED_ACTIVE_SESSION') return 'error';
    if (stage === 'STOPPED') return 'warning';
    return 'info';
};

const COLUMNS = [
    { field: 'otaId', headerName: 'OTA ID', flex: 1, sortable: false },
    {
        field: 'stage', headerName: 'Stage', flex: 0.9, sortable: false,
        renderCell: (params) => (
            <Chip label={params.row.stage ?? '—'} color={stageColor(params.row.stage)} size="small" />
        )
    },
    {
        field: 'completedDevices', headerName: 'Devices', flex: 0.6, sortable: false,
        renderCell: (params) => `${params.row.completedDevices ?? 0} / ${params.row.totalDevices ?? 0}`
    },
    { field: 'updatedBy', headerName: 'By', flex: 0.7, sortable: false,
        renderCell: (params) => params.row.updatedBy ?? '—' },
    {
        field: 'createdAt', headerName: 'Started', flex: 1, sortable: false,
        renderCell: (params) => params.row.createdAt
            ? moment(params.row.createdAt).format('DD-MM-YYYY HH:mm:ss')
            : '—'
    },
];

function GatewaySessionHistory({ gatewayId, coordinatorVersion }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [selectedOtaId, setSelectedOtaId] = useState(null);
    const [openDetail, setOpenDetail] = useState(false);
    const [snack, setSnack] = useState({ open: false, severity: 'error', message: '' });

    const fetchSessions = () => {
        setLoading(true);
        ApiServices.getGatewayOtaSessions(gatewayId, page, pageSize)
            .then(response => {
                if (response.status === 204 || !response.data) {
                    setSessions([]);
                    setTotal(0);
                } else {
                    setSessions(response.data.content ?? []);
                    setTotal(response.data.totalElements ?? 0);
                }
            })
            .catch(error => {
                setSnack({ open: true, severity: 'error', message: error.response?.data ?? error.message });
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchSessions();
    }, [page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRowClick = (params) => {
        setSelectedOtaId(params.row.otaId);
        setOpenDetail(true);
    };

    return (
        <Box>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={3000}
                open={snack.open}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
            >
                <Alert severity={snack.severity} sx={{ width: '100%' }}>{snack.message}</Alert>
            </Snackbar>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50]}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress /></Box>
            ) : (
                <DataGrid
                    rows={sessions}
                    columns={COLUMNS}
                    getRowId={(row) => row.id}
                    autoHeight
                    hideFooter
                    disableColumnMenu
                    disableSelectionOnClick
                    onRowClick={handleRowClick}
                    sx={{ cursor: 'pointer' }}
                    slots={{
                        noRowsOverlay: () => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography color="text.secondary">No sessions found</Typography>
                            </Box>
                        )
                    }}
                />
            )}

            {openDetail && (
                <Popup
                    title={`Session: ${selectedOtaId}`}
                    openPopup={openDetail}
                    setOpenPopup={setOpenDetail}
                >
                    <GatewaySessionDetail otaId={selectedOtaId} coordinatorVersion={coordinatorVersion} />
                </Popup>
            )}
        </Box>
    );
}

export default GatewaySessionHistory;
